/**
 * Inside the Formula.
 *
 * The ingredient circles are a Swiper carousel, and they lead: the arrows, a
 * click on a side circle, a drag and autoplay all move them. Every change
 * selects the same ingredient in the name, details and product title, which
 * fade as live's slick `fade` does; the stylesheet does all of the fading from
 * the `is-selected` class, and this only moves the class. A horizontal swipe on
 * that text moves the circles, as a swipe on live's text sliders does.
 *
 * Everything turns in live's 500ms (slick's default speed, which live never
 * changed). The two carousel styles are live's two sections:
 * - `classic` (live inside-formula, slick): `ease`, and the circles drag.
 * - `enhanced` (live inside-formula-epdp, its own track): the easing in the
 *   stylesheet, and the circles only move on a click or an arrow.
 * Both loop from three ingredients.
 *
 * The details box takes the new ingredient's height the way live's does: its
 * slick height animation fights a 0.3s CSS transition, so the box barely moves
 * during the 0.5s fade and then eases to size in 0.3s. The stylesheet delays
 * the transition; this sets the height.
 *
 * It also closes an open dose tooltip on an outside click, on Escape, and
 * when the ingredient changes.
 */
const SPEED = 500;

class InsideFormula extends HTMLElement {
  circles = null;
  resizeObserver = null;
  currentIndex = null;
  swipeStart = null;

  constructor() {
    super();
    this.onWindowLoad = this.onWindowLoad.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPointerCancel = this.onPointerCancel.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
  }

  connectedCallback() {
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('toggle', this.onToggle, true);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeydown);

    for (const fade of this.fades) {
      fade.addEventListener('pointerdown', this.onPointerDown);
      fade.addEventListener('pointerup', this.onPointerUp);
      fade.addEventListener('pointercancel', this.onPointerCancel);
    }

    // The layout loads Swiper with `defer`, so it is normally parsed before
    // this module runs. Fall back to the load event if it is not.
    if (typeof Swiper === 'undefined') {
      window.addEventListener('load', this.onWindowLoad, { once: true });
      return;
    }

    this.init();
  }

  disconnectedCallback() {
    window.removeEventListener('load', this.onWindowLoad);
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('toggle', this.onToggle, true);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);

    for (const fade of this.fades) {
      fade.removeEventListener('pointerdown', this.onPointerDown);
      fade.removeEventListener('pointerup', this.onPointerUp);
      fade.removeEventListener('pointercancel', this.onPointerCancel);
    }

    this.resizeObserver?.disconnect();
    this.circles?.destroy(true, true);
    this.circles = this.resizeObserver = this.currentIndex = this.swipeStart = null;
  }

  /** The name, details and product title: each a stack of one slide per ingredient. */
  get fades() {
    return this.querySelectorAll('[data-inside-formula-fade]');
  }

  get details() {
    return this.querySelector('[data-inside-formula-details]');
  }

  onWindowLoad() {
    this.init();
  }

  init() {
    const circlesElement = this.querySelector('[data-inside-formula-circles]');
    if (this.circles || !circlesElement) return;

    // Batch 6: the strip style moves a plain clamped track and needs no Swiper
    if (this.classList.contains('inside-formula--strip')) {
      this.circles = new StripTrack(this, circlesElement, (index) => this.select(index));
      this.select(this.circles.realIndex);
      this.observeDetails();
      return;
    }

    if (typeof Swiper === 'undefined') return;

    const enhanced = this.classList.contains('inside-formula--enhanced');
    const count = Number.parseInt(this.dataset.count, 10) || 0;
    const speed = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : SPEED;
    // Live's enhanced track loops from three ingredients, where none is ever on screen twice.
    // Slick loops any count; below three Swiper would show copies, so classic stops there too.
    const loop = count >= 3;
    const autoplayDelay = Number.parseInt(this.dataset.autoplayDelay, 10) || 5000;

    this.circles = new Swiper(circlesElement, {
      slidesPerView: 3,
      centeredSlides: true,
      loop,
      // The enhanced track keeps one whole copy on each side, as live's does; the centre slot's sub-pixel position
      // depends on how many slots precede it, so this also puts the circles where live paints them.
      loopedSlides: enhanced && loop ? count : null,
      speed,
      slideToClickedSlide: true,
      allowTouchMove: !enhanced,
      // Slick changes slide once a drag passes a fifth of the width.
      longSwipesRatio: 0.2,
      // With two ingredients Swiper would otherwise lock the carousel and hide both arrows.
      watchOverflow: false,
      navigation: {
        prevEl: this.querySelector('[data-inside-formula-prev]'),
        nextEl: this.querySelector('[data-inside-formula-next]'),
      },
      // Swiper's a11y module overwrites the arrows' aria-labels with these,
      // and its defaults are hardcoded English.
      a11y: {
        prevSlideMessage: this.dataset.previousLabel,
        nextSlideMessage: this.dataset.nextLabel,
      },
      autoplay:
        this.dataset.autoplay === 'true' && speed
          ? { delay: autoplayDelay, disableOnInteraction: false, pauseOnMouseEnter: true }
          : false,
      on: {
        slideChange: (swiper) => this.select(swiper.realIndex),
        // Live always comes to rest on its middle copy of the ingredients, as slick comes to rest on the real slide.
        // Swiper would stay on whichever copy it reached; move to the real one, which looks identical.
        slideChangeTransitionEnd: (swiper) => {
          if (swiper.params.loop) swiper.loopFix();
        },
      },
    });

    this.select(this.circles.realIndex);
    this.observeDetails();
  }

  /**
   * Shows ingredient `index` in the name, details and product title. The
   * others are inert, so keyboard focus never lands in an invisible slide.
   * @param {number} index
   */
  select(index) {
    if (index === this.currentIndex) return;

    const afterFade = this.currentIndex !== null;
    this.currentIndex = index;

    for (const fade of this.fades) {
      fade.querySelectorAll('.inside-formula__slide').forEach((slide, slideIndex) => {
        const selected = slideIndex === index;
        slide.classList.toggle('is-selected', selected);
        slide.inert = !selected;
      });
    }

    this.fitDetails(afterFade);
    this.closeTips();
  }

  /** @returns {HTMLElement | null} the details slide on show */
  get selectedDetail() {
    return this.details?.querySelector('.inside-formula__slide.is-selected') ?? null;
  }

  /**
   * Sizes the details box to the shown ingredient. A slide change eases after
   * the fade (the stylesheet's delay); a reflow of the same slide (a resize, a
   * late font) eases at once, as live's does.
   * @param {boolean} afterFade
   */
  fitDetails(afterFade) {
    const details = this.details;
    const slide = this.selectedDetail;
    if (!details || !slide) return;

    details.toggleAttribute('data-after-fade', afterFade);
    // offsetHeight rounds, as live's slick height does.
    details.style.height = `${slide.offsetHeight}px`;
  }

  observeDetails() {
    if (!this.details || !('ResizeObserver' in window)) return;

    this.resizeObserver = new ResizeObserver((entries) => {
      if (entries.some((entry) => entry.target === this.selectedDetail)) this.fitDetails(false);
    });

    for (const slide of this.details.querySelectorAll('.inside-formula__slide')) {
      this.resizeObserver.observe(slide);
    }
  }

  /** @param {PointerEvent} event */
  onPointerDown(event) {
    if (!event.isPrimary) return;

    this.swipeStart = { x: event.clientX, width: event.currentTarget.clientWidth };
  }

  /**
   * A horizontal swipe on the text past a fifth of its width, as slick's
   * threshold: next ingredient to the left, previous to the right.
   * @param {PointerEvent} event
   */
  onPointerUp(event) {
    const start = this.swipeStart;
    this.swipeStart = null;
    if (!start || !this.circles) return;

    const distance = event.clientX - start.x;
    if (Math.abs(distance) < start.width / 5) return;

    if (distance < 0) {
      this.circles.slideNext();
    } else {
      this.circles.slidePrev();
    }
  }

  onPointerCancel() {
    this.swipeStart = null;
  }

  /**
   * Live's slick moves on the arrow keys while focus is inside one of its sliders (the dose tooltip button, here),
   * not on its arrow buttons.
   * @param {KeyboardEvent} event
   */
  onKeydown(event) {
    if (!this.circles || !(event.target instanceof Element)) return;
    if (!event.target.closest('.inside-formula__circles, [data-inside-formula-fade]')) return;

    if (event.key === 'ArrowLeft') this.circles.slidePrev();
    if (event.key === 'ArrowRight') this.circles.slideNext();
  }

  /**
   * One dose tooltip open at a time.
   * @param {Event} event
   */
  onToggle(event) {
    const tip = event.target;
    if (!(tip instanceof HTMLDetailsElement) || !tip.matches('[data-inside-formula-tip]') || !tip.open) return;

    this.closeTips(tip);
  }

  /** @param {MouseEvent} event */
  onDocumentClick(event) {
    const inside = event.target instanceof Element ? event.target.closest('[data-inside-formula-tip]') : null;
    this.closeTips(inside);
  }

  /** @param {KeyboardEvent} event */
  onDocumentKeydown(event) {
    if (event.key === 'Escape') this.closeTips();
  }

  /** @param {Element | null} [except] */
  closeTips(except = null) {
    for (const tip of this.querySelectorAll('[data-inside-formula-tip][open]')) {
      if (tip !== except) tip.open = false;
    }
  }
}

/* Batch 6: the strip style's circles.
 *
 * Live's ee-inside-formula carousel: one active circle and its neighbours on a flex track that is translated to centre the
 * active one, clamped at both ends (no loop), 0.4s ease (in the stylesheet). The arrows, a click on a neighbour, a drag of
 * 40px or more on the circles and the arrow keys all move it. It has the small surface the section reads from a Swiper
 * (`realIndex`, `slideNext`, `slidePrev`, `destroy`), and reports each change to `onChange`.
 */
class StripTrack {
  /**
   * @param {HTMLElement} host the inside-formula element
   * @param {HTMLElement} window_ the clipped window the track slides in
   * @param {(index: number) => void} onChange
   */
  constructor(host, window_, onChange) {
    this.host = host;
    this.window = window_;
    this.onChange = onChange;
    this.track = window_.querySelector('.inside-formula__strip-track');
    this.slides = [...this.track.children];
    this.realIndex = 0;
    this.pointerX = null;
    this.prev = host.querySelector('[data-inside-formula-prev]');
    this.next = host.querySelector('[data-inside-formula-next]');

    this.onPrev = () => this.slidePrev();
    this.onNext = () => this.slideNext();
    this.onClick = (event) => {
      const slide = event.target instanceof Element ? event.target.closest('.inside-formula__circle') : null;
      const index = slide ? this.slides.indexOf(slide) : -1;
      if (index > -1) this.goTo(index);
    };
    this.onPointerDown = (event) => {
      if (event.isPrimary) this.pointerX = event.clientX;
    };
    this.onPointerUp = (event) => {
      if (this.pointerX === null) return;
      const distance = event.clientX - this.pointerX;
      this.pointerX = null;
      if (Math.abs(distance) < 40) return;
      if (distance < 0) this.slideNext();
      else this.slidePrev();
    };
    this.onPointerCancel = () => {
      this.pointerX = null;
    };

    this.prev?.addEventListener('click', this.onPrev);
    this.next?.addEventListener('click', this.onNext);
    window_.addEventListener('click', this.onClick);
    window_.addEventListener('pointerdown', this.onPointerDown);
    window_.addEventListener('pointerup', this.onPointerUp);
    window_.addEventListener('pointercancel', this.onPointerCancel);

    // The window's width (and so the centring) changes with the viewport; a late font or image can too
    this.resizeObserver = 'ResizeObserver' in globalThis ? new ResizeObserver(() => this.position(true)) : null;
    this.resizeObserver?.observe(window_);

    this.update();
    this.position(true);
  }

  slideNext() {
    this.goTo(this.realIndex + 1);
  }

  slidePrev() {
    this.goTo(this.realIndex - 1);
  }

  /** @param {number} index clamped to the first and last circle */
  goTo(index) {
    const next = Math.max(0, Math.min(index, this.slides.length - 1));
    if (next === this.realIndex) return;

    this.realIndex = next;
    this.update();
    this.position(false);
    this.onChange(next);
  }

  /** Marks the active circle and disables the arrow that has nowhere to go. */
  update() {
    this.slides.forEach((slide, index) => slide.classList.toggle('is-active', index === this.realIndex));
    if (this.prev) this.prev.disabled = this.realIndex <= 0;
    if (this.next) this.next.disabled = this.realIndex >= this.slides.length - 1;
  }

  /**
   * Centres the active circle in the window: the circles before it are side circles, and the gap follows each.
   * @param {boolean} instant skip the 0.4s slide (first paint, a resize)
   */
  position(instant) {
    const sizes = getComputedStyle(this.host);
    const active = Number.parseFloat(sizes.getPropertyValue('--inside-formula-strip-active')) || 0;
    const side = Number.parseFloat(sizes.getPropertyValue('--inside-formula-strip-side')) || 0;
    const gap = Number.parseFloat(sizes.getPropertyValue('--inside-formula-strip-gap')) || 0;
    const offset = this.window.clientWidth / 2 - (this.realIndex * (side + gap) + active / 2);

    if (instant) this.track.style.transition = 'none';
    this.track.style.transform = `translate3d(${offset}px, 0, 0)`;
    if (instant) {
      this.track.getBoundingClientRect();
      this.track.style.transition = '';
    }
  }

  /** @param {boolean} [_destroyDom] the Swiper signature; there is no DOM to take apart */
  destroy() {
    this.prev?.removeEventListener('click', this.onPrev);
    this.next?.removeEventListener('click', this.onNext);
    this.window.removeEventListener('click', this.onClick);
    this.window.removeEventListener('pointerdown', this.onPointerDown);
    this.window.removeEventListener('pointerup', this.onPointerUp);
    this.window.removeEventListener('pointercancel', this.onPointerCancel);
    this.resizeObserver?.disconnect();
  }
}

if (!customElements.get('inside-formula')) {
  customElements.define('inside-formula', InsideFormula);
}
